// Default value for a single schema node: const/default win, then oneOf (first
// branch), object (recurse), array ([]); scalars without a default get null.
export function buildDefaultValue(schema: any): unknown {
  if (!schema || typeof schema !== "object") return null;
  if (schema.const !== undefined) return schema.const;
  if (schema.default !== undefined) return schema.default;
  if (Array.isArray(schema.oneOf) && schema.oneOf.length > 0) {
    return buildRequiredDefaults(schema.oneOf[0]);
  }
  if (schema.type === "object" || schema.properties) return buildRequiredDefaults(schema);
  if (schema.type === "array") return schema.default ?? [];
  return null;
}

// Seed only the required properties of an object schema; scalar required fields
// without a default are left absent so Ajv still flags them as missing.
export function buildRequiredDefaults(schema: any): Record<string, unknown> {
  const required: string[] = Array.isArray(schema?.required) ? schema.required : [];
  const props: Record<string, any> = schema?.properties ?? {};
  const out: Record<string, unknown> = {};
  for (const key of required) {
    const propSchema = props[key];
    if (!propSchema) continue;
    if (propSchema.const !== undefined) {
      out[key] = propSchema.const;
    } else if (propSchema.default !== undefined) {
      out[key] = propSchema.default;
    } else if (Array.isArray(propSchema.oneOf) && propSchema.oneOf.length > 0) {
      out[key] = buildRequiredDefaults(propSchema.oneOf[0]);
    } else if (propSchema.type === "object" || propSchema.properties) {
      out[key] = buildRequiredDefaults(propSchema);
    } else if (propSchema.type === "array") {
      out[key] = [];
    }
  }
  return out;
}

export function seedRequiredDefaults(schema: any, data: any): any {
  if (!schema || typeof schema !== "object") return data;
  if (!schema.properties) return data ?? {};
  const out: Record<string, unknown> = { ...(data ?? {}) };
  const required: string[] = Array.isArray(schema.required) ? schema.required : [];
  const props = schema.properties as Record<string, any>;

  for (const key of Object.keys(props)) {
    const propSchema = props[key];
    const isRequired = required.includes(key);
    const present = key in out;

    if (isRequired && !present) {
      if (propSchema.default !== undefined) {
        out[key] = propSchema.default;
      } else if (Array.isArray(propSchema.oneOf) && propSchema.oneOf.length > 0) {
        out[key] = buildRequiredDefaults(propSchema.oneOf[0]);
      } else if (propSchema.type === "object" || propSchema.properties) {
        out[key] = seedRequiredDefaults(propSchema, {});
      } else if (propSchema.type === "array") {
        out[key] = [];
      } else if (propSchema.const !== undefined) {
        out[key] = propSchema.const;
      }
    } else if (isRequired && present) {
      const value = out[key];
      if (
        value &&
        typeof value === "object" &&
        !Array.isArray(value) &&
        propSchema.properties
      ) {
        out[key] = seedRequiredDefaults(propSchema, value);
      }
    }
  }
  return out;
}
